/**
 * Hover-to-open dropdowns for the desktop header menu.
 *
 * Replaces an earlier inline script in sections/header.liquid which:
 *   - registered its mouseleave listeners *inside* the mouseover handler, so
 *     every hover added another pair (unbounded listener growth);
 *   - used `mouseover`, which re-fires as the pointer crosses child elements;
 *   - set/removed the `open` attribute directly, leaving `aria-expanded` stale
 *     so screen readers were told the menu was still collapsed;
 *   - threw a TypeError when `.header__inline-menu` was absent (e.g. when the
 *     header is set to the drawer menu type), killing later inline scripts.
 *
 * Behaviour here:
 *   - pointer-aware: only binds where hover genuinely exists, so touch devices
 *     keep tap-to-open and never get a menu stuck open;
 *   - desktop-only, matching the breakpoint at which Dawn shows the inline menu;
 *   - a short close delay lets the pointer travel diagonally into the submenu
 *     without it snapping shut;
 *   - keeps `aria-expanded` in sync, and defers to Dawn's own `close()` so the
 *     sticky-header state it maintains stays correct;
 *   - never closes while keyboard focus is inside; Escape closes and restores
 *     focus to the summary;
 *   - click still works exactly as before - this only adds hover.
 */
(function () {
  'use strict';

  var CLOSE_DELAY = 120; // ms - enough to cross the gap to the submenu
  var DESKTOP = '(min-width: 990px)';
  var HOVERABLE = '(hover: hover) and (pointer: fine)';
  var BOUND = 'hoverMenuBound';

  function canHover() {
    return window.matchMedia(HOVERABLE).matches && window.matchMedia(DESKTOP).matches;
  }

  function open(details) {
    if (details.hasAttribute('open')) return;
    details.setAttribute('open', '');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  function close(details) {
    if (!details.hasAttribute('open')) return;
    var host = details.closest('header-menu, details-disclosure');
    // Prefer Dawn's own API: it clears `open` *and* aria-expanded, and lets the
    // custom element keep its sticky-header bookkeeping in step.
    if (host && typeof host.close === 'function') {
      host.close();
      return;
    }
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function bind(menu) {
    menu.querySelectorAll('details').forEach(function (details) {
      if (details.dataset[BOUND]) return; // never double-bind
      details.dataset[BOUND] = 'true';

      var timer = null;
      var cancel = function () {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      };

      details.addEventListener('mouseenter', function () {
        if (!canHover()) return;
        cancel();
        // Close any sibling that is still open so only one panel shows.
        menu.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== details) close(other);
        });
        open(details);
      });

      details.addEventListener('mouseleave', function () {
        if (!canHover()) return;
        cancel();
        timer = setTimeout(function () {
          // A keyboard user may have tabbed in while the pointer drifted away.
          if (details.contains(document.activeElement)) return;
          close(details);
        }, CLOSE_DELAY);
      });

      details.addEventListener('keyup', function (event) {
        if (event.key !== 'Escape') return;
        cancel();
        close(details);
        var summary = details.querySelector('summary');
        if (summary) summary.focus();
      });
    });
  }

  function init() {
    // Null-safe: the inline menu is absent for the drawer header type.
    document.querySelectorAll('.header__inline-menu').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the header section; re-bind the fresh nodes.
  document.addEventListener('shopify:section:load', init);
})();
